import { useCallback, useEffect, useState } from 'react';

/*
 * Captura a localização do aparelho pelo navegador (navigator.geolocation),
 * portado do take-saúde (features/attendance-signature/useGeolocation.ts).
 *
 * Nunca lança: em negação/indisponibilidade resolve um objeto com `source`
 * explicando o motivo, e quem chama decide o que fazer. Aqui no ponto a
 * decisão é sempre BLOQUEAR: sem localização não há registro.
 *
 * Também expõe o estado da permissão (Permissions API) para a tela rotear a
 * mensagem antes do clique: pedir na hora, avisar que vai pedir, ou mandar
 * reativar nas configurações do navegador.
 */

/** Navegador "de dentro" de um app (Instagram, Facebook…), onde o pedido de localização costuma não abrir nada. */
export function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return { inApp: false };
  const ua = navigator.userAgent || '';
  if (/Instagram/i.test(ua)) return { inApp: true, name: 'Instagram' };
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return { inApp: true, name: 'Facebook' };
  if (/\bMicroMessenger\b/i.test(ua)) return { inApp: true, name: 'WeChat' };
  if (/; wv\)/i.test(ua)) return { inApp: true };
  return { inApp: false };
}

export const geoDisponivel = () =>
  typeof navigator !== 'undefined' && 'geolocation' in navigator;

/** "85 m", "1,2 km", "12 km" — porte 1:1 de backend/src/common/geo.ts. */
export function formatDistance(meters) {
  if (meters == null || !Number.isFinite(Number(meters))) return '';
  const m = Math.round(Number(meters));
  if (m < 1000) return `${m} m`;
  const km = m / 1000;
  // Decide o formato DEPOIS de arredondar: 9.960 m tem que sair "10 km", não "10,0 km".
  const umaCasa = km.toFixed(1);
  if (Number(umaCasa) >= 10) return `${Math.round(km)} km`;
  return `${umaCasa.replace('.', ',')} km`;
}

/** Mensagem para o militar conforme o motivo da falha. */
export function mensagemGeoFalha(source) {
  switch (source) {
    case 'denied':
      return 'Localização negada. Permita o acesso à localização para registrar o ponto.';
    case 'unavailable':
      return 'Não foi possível obter sua localização. Ative o GPS/localização do aparelho e tente de novo.';
    default:
      return 'A localização não respondeu a tempo. Tente de novo em local aberto ou com o GPS ligado.';
  }
}

export default function useGeolocation() {
  const [state, setState] = useState('idle'); // idle | requesting | captured | error
  const [permission, setPermission] = useState('unknown'); // unknown | granted | prompt | denied | unsupported

  const checkPermission = useCallback(async () => {
    if (typeof navigator === 'undefined') return 'unsupported';
    const perms = navigator.permissions;
    if (!perms || !perms.query) {
      setPermission('unsupported');
      return 'unsupported';
    }
    const map = (s) => (s === 'granted' ? 'granted' : s === 'denied' ? 'denied' : 'prompt');
    try {
      const status = await perms.query({ name: 'geolocation' });
      setPermission(map(status.state));
      // Reflete na hora se a pessoa mudar a permissão nas configurações.
      status.onchange = () => setPermission(map(status.state));
      return map(status.state);
    } catch {
      setPermission('unsupported');
      return 'unsupported';
    }
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const request = useCallback(() => {
    if (!geoDisponivel()) {
      setState('error');
      return Promise.resolve({ source: 'unavailable' });
    }
    setState('requesting');
    return new Promise((resolve) => {
      // `fromFallback` marca leitura de precisão BAIXA (rede/wifi/IP) — não é
      // GPS e fica registrada como "network" para a auditoria saber.
      const onOk = (fromFallback) => (pos) => {
        // (0, 0) é GPS mal lido (alguns WebViews/emuladores devolvem isso):
        // vale como "não conseguiu se localizar", não como posição.
        if (pos.coords.latitude === 0 && pos.coords.longitude === 0) {
          setState('error');
          resolve({ source: 'unavailable' });
          return;
        }
        const c = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: fromFallback ? 'network' : 'gps',
        };
        setState('captured');
        setPermission('granted');
        resolve(c);
      };
      // Erro de precisão ALTA que não seja "negado" (timeout/indisponível) é
      // comum em celular (GPS demora a fixar dentro do prédio). Antes de
      // desistir, tenta com precisão BAIXA, que quase sempre resolve.
      const onError = (isHighAccuracyAttempt) => (err) => {
        if (isHighAccuracyAttempt && err.code !== err.PERMISSION_DENIED) {
          navigator.geolocation.getCurrentPosition(onOk(true), onError(false), {
            enableHighAccuracy: false,
            timeout: 20000,
            // Sem cache: o ponto tem que registrar ONDE ele foi batido agora.
            maximumAge: 0,
          });
          return;
        }
        const source =
          err.code === err.PERMISSION_DENIED
            ? 'denied'
            : err.code === err.POSITION_UNAVAILABLE
              ? 'unavailable'
              : 'error';
        setState('error');
        if (source === 'denied') setPermission('denied');
        resolve({ source });
      };
      navigator.geolocation.getCurrentPosition(onOk(false), onError(true), {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    });
  }, []);

  return { state, permission, request, checkPermission };
}
